import React, { useState, useRef, useEffect, useCallback } from 'react';
import { IonPage, IonContent, IonButton } from '@ionic/react';
import { configService } from '../services/ConfigService';
import MascotMessage from '../components/MascotMessage';
import { soundService } from '../services/SoundService';
import { hapticService } from '../services/HapticService';
import './Onboarding.css';

/**
 * Onboarding Page - Modern, Attractive First-time User Welcome Flow
 * Features: Animated gradients, glass-morphism cards, swipe gestures, smooth transitions
 */
function Onboarding({ onComplete }) {
  const [currentStep, setCurrentStep] = useState(0);
  const [touchStart, setTouchStart] = useState(null);
  const [touchEnd, setTouchEnd] = useState(null);
  const [isAnimating, setIsAnimating] = useState(false);
  const containerRef = useRef(null);
  
  // Default steps if config not available
  const onboardingConfig = configService.getOnboarding();
  const steps = onboardingConfig?.steps || [
    {
      title: "Welcome! 👋",
      description: "Learn to code from scratch with fun, visual lessons!",
      image: "onboarding/welcome.svg",
      mascotState: "wave",
      emoji: "🎉"
    },
    {
      title: "Learn Visually 🎨",
      description: "See concepts come to life with animations and illustrations!",
      image: "onboarding/visual-learning.svg",
      mascotState: "explain",
      emoji: "✨"
    },
    {
      title: "Practice & Code 💻",
      description: "Write real code and see instant results!",
      image: "onboarding/practice.svg",
      mascotState: "happy",
      emoji: "🚀"
    },
    {
      title: "Earn Rewards 🏆",
      description: "Complete lessons, earn badges, and track your progress!",
      image: "onboarding/rewards.svg",
      mascotState: "celebrate",
      emoji: "🌟"
    }
  ];

  // Minimum swipe distance for gesture detection
  const minSwipeDistance = 50;

  const onTouchStart = (e) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  };

  const onTouchMove = (e) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const onTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    
    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > minSwipeDistance;
    const isRightSwipe = distance < -minSwipeDistance;
    
    if (isLeftSwipe && currentStep < steps.length - 1) {
      goToStep(currentStep + 1);
    } else if (isRightSwipe && currentStep > 0) {
      goToStep(currentStep - 1);
    }
  };

  const goToStep = useCallback((stepIndex) => {
    if (isAnimating || stepIndex < 0 || stepIndex >= steps.length) return;
    
    setIsAnimating(true);
    soundService.playClick();
    hapticService.light();
    
    setCurrentStep(stepIndex);
    
    setTimeout(() => {
      setIsAnimating(false);
    }, 500);
  }, [isAnimating, steps.length]);

  const handleNext = () => {
    if (currentStep < steps.length - 1) {
      goToStep(currentStep + 1);
    } else {
      handleComplete();
    }
  };

  const handleComplete = () => {
    soundService.playSuccess();
    hapticService.success();
    onComplete();
  };

  const handleSkip = () => {
    soundService.playClick();
    hapticService.light();
    onComplete();
  };

  const handleDotClick = (index) => {
    if (index !== currentStep) {
      goToStep(index);
    }
  };

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'ArrowRight' || e.key === 'Enter') {
        handleNext();
      } else if (e.key === 'ArrowLeft' && currentStep > 0) {
        goToStep(currentStep - 1);
      } else if (e.key === 'Escape') {
        handleSkip();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [currentStep, goToStep]);

  const getSlideClass = (index) => {
    if (index === currentStep) return 'slide active';
    if (index < currentStep) return 'slide prev';
    return 'slide next';
  };

  const isLastStep = currentStep === steps.length - 1;

  return (
    <IonPage>
      <IonContent scrollY={false} className="onboarding-content">
        <div 
          ref={containerRef}
          className="onboarding-container"
          onTouchStart={onTouchStart}
          onTouchMove={onTouchMove}
          onTouchEnd={onTouchEnd}
        >
          {/* Animated Gradient Background */}
          <div className="onboarding-background" />

          {/* Skip Button */}
          {onboardingConfig?.skipEnabled !== false && (
            <button className="skip-btn" onClick={handleSkip}>
              Skip
            </button>
          )}

          {/* Slides Container */}
          <div className="slides-wrapper">
            {steps.map((step, index) => (
              <div key={index} className={getSlideClass(index)}>
                {/* Image */}
                <div className="slide-image-container">
                  {step.image ? (
                    <img 
                      src={`/assets/media/${step.image}`} 
                      alt={step.title}
                      className="slide-image"
                      onError={(e) => {
                        e.target.style.display = 'none';
                        e.target.nextSibling.style.display = 'flex';
                      }}
                    />
                  ) : null}
                  <div className="slide-image-placeholder" style={{ display: step.image ? 'none' : 'flex' }}>
                    {step.emoji || '📚'}
                  </div>
                </div>

                {/* Glass Card */}
                <div className="slide-card">
                  {/* Mascot */}
                  <div className="slide-mascot">
                    <MascotMessage
                      state={step.mascotState || 'wave'}
                      size="small"
                      animate={index === currentStep}
                    />
                  </div>

                  {/* Title */}
                  <h1 className="slide-title">{step.title}</h1>
                  
                  {/* Description */}
                  <p className="slide-description">{step.description}</p>
                </div>
              </div>
            ))}
          </div>

          {/* Swipe Hint (only on first step) */}
          {currentStep === 0 && steps.length > 1 && (
            <div className="swipe-hint">
              <span>Swipe to explore</span>
              <span className="swipe-hint-icon">→</span>
            </div>
          )}

          {/* Footer */}
          <div className="onboarding-footer">
            {/* Progress Dots */}
            <div className="progress-dots">
              {steps.map((_, index) => (
                <div 
                  key={index}
                  className={`dot ${index === currentStep ? 'active' : ''} ${index < currentStep ? 'completed' : ''}`}
                  onClick={() => handleDotClick(index)}
                  role="button"
                  aria-label={`Go to step ${index + 1}`}
                  tabIndex={0}
                  onKeyDown={(e) => e.key === 'Enter' && handleDotClick(index)}
                />
              ))}
            </div>

            {/* Action Button */}
            <div className="onboarding-actions">
              <IonButton 
                expand="block" 
                onClick={handleNext}
                className={`next-btn ${isLastStep ? 'final-step' : ''}`}
                disabled={isAnimating}
              >
                {isLastStep ? "Let's Start! 🚀" : "Next"}
              </IonButton>
            </div>
          </div>
        </div>
      </IonContent>
    </IonPage>
  );
}

export default Onboarding;